import { useEffect, useState } from "react";
import { Mail, ChevronDown, ChevronUp, Users, ShoppingCart, Briefcase } from "lucide-react";

const FAQPage = () => {
  const [openSection, setOpenSection] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const askSection = document.getElementById("ask-anything");
      if (askSection) {
        askSection.scrollIntoView({ behavior: "smooth" });
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const toggleSection = (section: any) => {
    setOpenSection(openSection === section ? null : section);
  };

  const faqSections = [
    {
      id: 'buyers',
      title: 'For Buyers',
      icon: ShoppingCart,
      questions: [
        {
          q: "How do I buy a product?",
          a: "Search for a listing, connect with the provider, and use our secure escrow system for payment."
        },
        {
          q: "Is payment safe?",
          a: "For a protected deal, funds are held while the deal progresses. After delivery is marked, use the review window shown in the deal tracker to confirm completion or raise a dispute. Eligible funds can be released automatically when that window expires without an open dispute. Payment protection does not replace checking the provider and agreeing clear terms."
        },
        {
          q: "What fees will I pay, including when adding to an order?",
          a: "Review and Pay shows the order amount, payer platform fee, and total before you pay. An add-to-order payment has its own fee breakdown too. A provider's first-deal fee waiver does not waive the payer's platform fee. Check the displayed quote for the amount that applies to your payment."
        },
        {
          q: "Can I cancel after payment?",
          a: "The payer can request cancellation when the deal tracker offers it, before a physical order is shipped or the work is marked delivered. An open add-to-order request must be completed or declined first. The provider can accept or decline; if they do not respond by the displayed deadline, an eligible refund is queued automatically."
        },
        {
          q: "What if there is a problem after delivery?",
          a: "Raise a dispute within the review window shown in the deal tracker and describe the issue in chat. An open dispute blocks payout. If a refund is agreed and the provider approves it, the refund can be queued while funds are still held. Automatic refunds are blocked once provider payout has begun."
        },
        {
          q: "Does refund-processing mean my refund has arrived?",
          a: "No. It means the refund is being processed through the payment provider. The final status is updated after provider confirmation. Bank processing times can vary; use the deal tracker and contact support if you need help."
        },
        {
          q: "Can I report a provider or another user?",
          a: "Yes. Use Report on their post or profile and choose the closest reason. You can also block the user to prevent new contact."
        }
      ]
    },
    {
      id: 'sellers',
      title: 'For Sellers',
      icon: Briefcase,
      questions: [
        {
          q: "How do I list a product or service?",
          a: "Choose Add Post, describe your product or service, and select the appropriate deal type. Complete the required KYC to receive protected deal payments."
        },
        {
          q: "How do I get paid?",
          a: "Eligible held funds are released after payer completion or expiry of the delivery review window without an open dispute. Payment-processing is not confirmation of bank credit. The deal is marked completed after the payment provider confirms payout success."
        },
        {
          q: "Are there any fees?",
          a: "The payment request and deal tracker show your estimated payout after applicable provider fees. A qualifying first deal may receive a provider platform-fee waiver; follow the quote shown for your deal. Additional order amounts also affect the final payout. Payer fees are separate."
        }
      ]
    },
    {
      id: 'bizz',
      title: 'For Marketing & Skill Users',
      icon: Users,
      questions: [
        {
          q: "Can I post job or service offers?",
          a: "Yes, Busiconn supports posting of skills, freelance services, and business offers in the relevant section."
        },
        {
          q: "How do I get clients?",
          a: "Keep your profile active, share listings, and engage with businesses through chat or calls."
        },
        {
          q: "Can I promote my business?",
          a: "Yes! You can post product/service deals and promote your business page on Busiconn for visibility."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-gray-600 text-lg">
            Find answers to common questions about Busiconn
          </p>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6 mb-16">
          {faqSections.map((section) => (
            <div
              key={section.id}
              className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
            >
              {/* Section Header */}
              <button
                aria-expanded={openSection === section.id}
                aria-controls={`faq-${section.id}`}
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white flex items-center justify-between hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
              >
                <div className="flex items-center gap-3">
                  <section.icon className="h-6 w-6" />
                  <h2 className="text-xl font-semibold">{section.title}</h2>
                </div>
                {openSection === section.id ? (
                  <ChevronUp className="h-5 w-5" />
                ) : (
                  <ChevronDown className="h-5 w-5" />
                )}
              </button>

              {/* Section Content */}
                <div id={`faq-${section.id}`} hidden={openSection !== section.id} className="px-6 py-4 animate-in slide-in-from-top-2 duration-200">
                  <div className="space-y-6">
                    {section.questions.map((item, index) => (
                      <div key={index} className="border-l-4 border-blue-500 pl-4">
                        <h3 className="font-semibold text-gray-800 mb-2">
                          {item.q}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {item.a}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
            </div>
          ))}
        </div>

        {/* Interactive Support Hub */}
        <div id="ask-anything" className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-teal-600 px-6 py-4">
            <div className="flex items-center gap-3 text-white">
              <Mail className="h-6 w-6" />
              <h2 className="text-2xl font-bold">Get Support - Multiple Ways!</h2>
            </div>
          </div>
          
          <div className="px-6 py-6">
            <p className="text-gray-600 mb-8 leading-relaxed">
              Choose your preferred way to get help. We're here to support your business success!
            </p>
            
            {/* Support Options Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Email Support */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="hidden" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.886 3.75"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">Email Support</h3>
                    <p className="text-sm text-gray-600">Questions and account assistance</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4 text-sm">
                  Email our team for help with your account or the Busiconn platform.
                </p>
                <a
                  href="mailto:contact@busiconn.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                >
                  <svg className="hidden" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.886 3.75"/>
                  </svg>
                  Email Support
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-8 text-gray-500">
          <p>Need immediate help? Email us directly at <strong>contact@busiconn.com</strong></p>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
